import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Avatar } from '@/components/ui/Avatar'
import { Spinner } from '@/components/ui/Spinner'
import { useAdmins } from '@/hooks/useAdmins'
import { useAssignClient } from '@/hooks/useAssignClient'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Client } from '@/types/client'

interface AssignAdminModalProps {
  client: Client
  isOpen: boolean
  onClose: () => void
}

export function AssignAdminModal({ client, isOpen, onClose }: AssignAdminModalProps) {
  const { data: admins, isLoading: loadingAdmins } = useAdmins()
  const assignClient = useAssignClient()

  const currentOwnerId = client.assigned_admins?.find((a) => a.is_owner)?.admin_id
  const [selectedAdmin, setSelectedAdmin] = useState<string | undefined>(currentOwnerId)

  const handleAssign = async () => {
    if (!selectedAdmin) return

    try {
      await assignClient.mutateAsync({
        clientId: client.id,
        adminId: selectedAdmin,
        previousAdminId: currentOwnerId,
      })
      onClose()
    } catch (error) {
      console.error('Failed to assign client:', error)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Attribuer le client" size="md">
      <div className="space-y-4">
        <p className="text-brown-600">
          Selectionnez l'admin responsable de <strong>{client.full_name}</strong>
        </p>

        {loadingAdmins ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {admins?.map((admin) => (
              <label
                key={admin.id}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                  selectedAdmin === admin.id
                    ? 'border-mint-500 bg-mint-50'
                    : 'border-beige-200 hover:bg-beige-50'
                )}
              >
                <input
                  type="radio"
                  name="admin"
                  value={admin.id}
                  checked={selectedAdmin === admin.id}
                  onChange={() => setSelectedAdmin(admin.id)}
                  className="sr-only"
                />
                <Avatar src={admin.avatar_url} name={admin.full_name} size="md" />
                <div className="flex-1">
                  <p className="font-medium text-brown-900">{admin.full_name}</p>
                  <p className="text-sm text-brown-500">{admin.clients_count} clients</p>
                </div>
                {selectedAdmin === admin.id && (
                  <Check className="w-5 h-5 text-mint-500" />
                )}
              </label>
            ))}
          </div>
        )}

        <div className="flex gap-3 pt-4 border-t border-beige-200">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Annuler
          </Button>
          <Button
            onClick={handleAssign}
            className="flex-1"
            disabled={!selectedAdmin || selectedAdmin === currentOwnerId}
            isLoading={assignClient.isPending}
          >
            Attribuer
          </Button>
        </div>
      </div>
    </Modal>
  )
}
